// LoginPage.jsx
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export function LoginPage() {
  const { login, googleLogin } = useAuth();
  const navigate  = useNavigate();
  const [form, setForm] = useState({ identifier: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const googleButtonRef = useRef(null);
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (!googleClientId || !googleButtonRef.current) return undefined;

    let cancelled = false;

    const renderGoogleButton = () => {
      if (cancelled || !window.google?.accounts?.id || !googleButtonRef.current) return;

      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async ({ credential }) => {
          try {
            setLoading(true);
            const user = await googleLogin(credential);
            toast.success(`Welcome, ${user.name.split(' ')[0]}!`);
            navigate(user.role === 'admin' ? '/admin' : '/');
          } catch (err) {
            toast.error(err.response?.data?.message || 'Google sign-in failed');
          } finally {
            setLoading(false);
          }
        },
      });

      googleButtonRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'outline',
        size: 'large',
        shape: 'pill',
        text: 'continue_with',
        width: 360,
      });
      setGoogleReady(true);
    };

    if (window.google?.accounts?.id) {
      renderGoogleButton();
      return () => {
        cancelled = true;
      };
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = renderGoogleButton;
    document.body.appendChild(script);

    return () => {
      cancelled = true;
    };
  }, [googleClientId, googleLogin, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.identifier, form.password);
      toast.success(`Welcome back, ${user.name.split(' ')[0]}!`);
      navigate(user.role === 'admin' ? '/admin' : '/');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-yamass-cream flex items-center justify-center px-4">
      <div className="bg-white rounded-3xl shadow-sm p-10 w-full max-w-md">
        <h1 className="font-display text-3xl font-bold text-yamass-black mb-2">Welcome back</h1>
        <p className="text-gray-400 text-sm mb-8">Sign in to your YAMASS account</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Email</label>
            <input type="text" required value={form.identifier} onChange={e => setForm(f => ({...f, identifier: e.target.value}))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-yamass-accent" />
          </div>
          <div>
            <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Password</label>
            <input type="password" required value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-yamass-accent" />
            <div className="mt-2 text-right">
              <Link to="/forgot-password" className="text-sm text-yamass-accent font-medium hover:underline">
                Forgot password?
              </Link>
            </div>
          </div>
          <button type="submit" disabled={loading}
            className="w-full bg-yamass-black text-white py-4 rounded-full text-sm font-semibold hover:bg-yamass-accent transition-colors disabled:opacity-50 mt-2">
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-gray-300">
          <div className="h-px flex-1 bg-gray-200" />
          <span>Or</span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>
        {googleClientId ? (
          <div className="space-y-2">
            <div ref={googleButtonRef} className="flex justify-center" />
            {!googleReady && (
              <p className="text-center text-xs text-gray-400">Loading Google sign-in...</p>
            )}
          </div>
        ) : (
          <p className="text-center text-sm text-gray-400">
            Google sign-in will appear after `REACT_APP_GOOGLE_CLIENT_ID` is set.
          </p>
        )}
        <p className="text-center text-sm text-gray-400 mt-6">
          Don't have an account? <Link to="/register" className="text-yamass-accent font-medium hover:underline">Register</Link>
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
